import { useEffect, useState } from 'react';
import type { UseLiveUpdatesPausedArgs } from '@dealstack/frontend/shared/hooks/useLiveUpdatesPaused.types';

/**
 * EventSource is disconnected for a moment on first open and again on every
 * silent retry, so `isConnected` alone would flash a warning during normal
 * operation.
 */
export const LIVE_UPDATES_GRACE_PERIOD_MS = 5_000;

/**
 * Whether a dropped stream has lasted long enough to be worth telling the user
 * about. True only after `gracePeriodMs` of continuous disconnection, so a
 * connection that never establishes still surfaces — just not instantly.
 */
export const useLiveUpdatesPaused = ({
	isConnected,
	isSupported,
	gracePeriodMs = LIVE_UPDATES_GRACE_PERIOD_MS,
}: UseLiveUpdatesPausedArgs): boolean => {
	const [isPaused, setIsPaused] = useState(false);

	useEffect(() => {
		// Nothing to wait for — this browser will never connect.
		if (!isSupported) {
			setIsPaused(true);
			return undefined;
		}

		if (isConnected) {
			setIsPaused(false);
			return undefined;
		}

		const timeoutId = window.setTimeout(() => setIsPaused(true), gracePeriodMs);
		return () => window.clearTimeout(timeoutId);
	}, [isConnected, isSupported, gracePeriodMs]);

	return isPaused;
};
